import { SERVER_URL } from '@/config/serverUrl';
import { shopApi } from '@/shop/shopApi';
import { characterApi } from '@/characters/characterApi';
import { holdSkinsApi } from '@/holdskins/holdSkinsApi';
import { skillsApi } from '@/skills/skillsApi';
import { fetchIapProducts } from '@/iap/iapApi';
import { loadClimberTextures } from '@/game/textures';
import { preloadCharacterTextureUrls } from '@/game/entities/Climber';
import { loadHoldSkinTexture } from '@/holdskins/holdTexture';

export type AssetPrefetchPhase = 'manifest' | 'download' | 'textures' | 'complete';

export interface AssetPrefetchProgress {
  phase: AssetPrefetchPhase;
  loaded: number;
  total: number;
}

interface AssetManifest {
  images: string[];
  media: string[];
  characterImages: string[];
  holdImages: string[];
}

/**
 * Launch-time asset warm-up. The app waits for this task after authentication
 * so remote shop art, character animation frames, hold skins and finisher
 * videos are already in the WebView cache before any interactive screen opens.
 *
 * The task remains best-effort: individual failures count as finished and the
 * game falls back to lazy loading, so a missing admin upload cannot deadlock
 * the boot screen.
 */
let prefetchPromise: Promise<void> | null = null;
let progress: AssetPrefetchProgress = { phase: 'manifest', loaded: 0, total: 0 };
const listeners = new Set<(next: AssetPrefetchProgress) => void>();

// Finisher clips are retained as object URLs so WebGL playback starts without
// another request and without cross-origin texture taint.
const mediaBlobs = new Map<string, string>();

export function absoluteAssetUrl(url: string): string {
  if (!url) return url;
  if (/^(https?:)?\/\//.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/assets/')) return url;
  return `${SERVER_URL}${url}`;
}

/** Object URL for a preloaded finisher clip, or null if the clip was missing. */
export function getPreloadedMedia(url?: string | null): string | null {
  if (!url) return null;
  return mediaBlobs.get(absoluteAssetUrl(url)) ?? null;
}

function emit(next: AssetPrefetchProgress): void {
  progress = next;
  for (const listener of listeners) listener(next);
}

function allPoseUrls(character: {
  idleUrl: string | null;
  climbUrl: string | null;
  fallUrl: string | null;
  winUrl: string | null;
  idleFrames: string[] | null;
  climbFrames: string[] | null;
  fallFrames: string[] | null;
  winFrames: string[] | null;
}): Array<string | null> {
  return [
    character.idleUrl,
    character.climbUrl,
    character.fallUrl,
    character.winUrl,
    ...(character.idleFrames ?? []),
    ...(character.climbFrames ?? []),
    ...(character.fallFrames ?? []),
    ...(character.winFrames ?? []),
  ];
}

async function collectUrls(): Promise<AssetManifest> {
  const images = new Set<string>();
  const media = new Set<string>();
  const characterImages = new Set<string>();
  const holdImages = new Set<string>();
  const bossRewardFrameUrls = [
    '/assets/bosses/frames/stone-king.webp',
    '/assets/bosses/frames/frost-empress.webp',
    '/assets/bosses/frames/storm-roc.webp',
    '/assets/bosses/frames/abyss-kraken.webp',
    '/assets/bosses/frames/verdant-colossus.webp',
    '/assets/bosses/frames/inferno-wyrm.webp',
    '/assets/bosses/frames/eclipse-sovereign.webp',
    '/assets/bosses/frames/seven-seals.webp',
  ] as const;
  const addImage = (url?: string | null): void => {
    if (url) images.add(absoluteAssetUrl(url));
  };
  const addMedia = (url?: string | null): void => {
    if (url) media.add(absoluteAssetUrl(url));
  };
  const addCharacterImage = (url?: string | null): void => {
    if (!url) return;
    const resolved = absoluteAssetUrl(url);
    images.add(resolved);
    characterImages.add(resolved);
  };
  const addHoldImage = (url?: string | null): void => {
    if (!url) return;
    const resolved = absoluteAssetUrl(url);
    images.add(resolved);
    holdImages.add(resolved);
  };

  // These assets are bundled inside the web/native client. Keep their URLs
  // same-origin so Capacitor loads them from capacitor://localhost instead of
  // trying the remote API host.
  for (const url of [
    '/assets/home-nav/clan.png',
    '/assets/home-nav/boss.png',
    '/assets/home-nav/daily-quest.png',
  ]) {
    images.add(url);
  }

  for (const url of bossRewardFrameUrls) addImage(url);

  const [catalog, bundles, characters, holds, skillSkins, iapProducts] =
    await Promise.allSettled([
      shopApi.catalog(),
      shopApi.bundles(),
      characterApi.list(),
      holdSkinsApi.list(),
      skillsApi.skins(),
      fetchIapProducts(),
    ]);

  if (catalog.status === 'fulfilled') {
    for (const item of catalog.value.items) {
      addImage(item.iconUrl);
      if (item.slot === 'endgameFx') {
        addMedia((item.data ?? {}).mediaUrl as string | undefined);
      }
      if (item.slot === 'matchBackground') {
        addImage((item.data ?? {}).imageUrl as string | undefined);
      }
    }
  }

  if (bundles.status === 'fulfilled') {
    for (const bundle of bundles.value.items) {
      addImage(bundle.bannerUrl);
      for (const url of allPoseUrls(bundle.character)) addCharacterImage(url);
      addImage(bundle.avatarFrame.iconUrl);
      addImage(bundle.finisher.iconUrl);
      addMedia((bundle.finisher.data ?? {}).mediaUrl as string | undefined);
    }
  }

  if (characters.status === 'fulfilled') {
    for (const character of characters.value.items) {
      for (const url of allPoseUrls(character)) addCharacterImage(url);
    }
  }

  if (holds.status === 'fulfilled') {
    for (const url of holds.value.defaultUrls) addHoldImage(url);
    for (const item of holds.value.items) addHoldImage(item.imageUrl);
  }

  if (skillSkins.status === 'fulfilled') {
    addImage(skillSkins.value.disrupt);
    addImage(skillSkins.value.shield);
  }

  if (iapProducts.status === 'fulfilled') {
    for (const product of iapProducts.value) addImage(product.imageUrl);
  }

  return {
    images: [...images],
    media: [...media],
    characterImages: [...characterImages],
    holdImages: [...holdImages],
  };
}

async function warmMediaOne(url: string): Promise<void> {
  if (mediaBlobs.has(url)) return;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 45_000);
  try {
    const response = await fetch(url, {
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!response.ok) return;
    const blob = await response.blob();
    mediaBlobs.set(url, URL.createObjectURL(blob));
  } catch {
    /* Fall back to streaming when the clip is used. */
  } finally {
    window.clearTimeout(timeout);
  }
}

function warmImageOne(url: string): Promise<void> {
  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      image.onload = null;
      image.onerror = null;
      resolve();
    };
    const timeout = window.setTimeout(finish, 15_000);
    image.decoding = 'async';
    image.onload = finish;
    image.onerror = finish;
    image.src = url;
    if (image.complete) finish();
  });
}

async function warmPool(
  urls: string[],
  loader: (url: string) => Promise<void>,
  concurrency: number,
  onItemDone: () => void,
): Promise<void> {
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < urls.length) {
      const url = urls[cursor++];
      if (!url) continue;
      await loader(url);
      onItemDone();
    }
  };
  const laneCount = Math.min(concurrency, urls.length);
  if (laneCount <= 0) return;
  await Promise.all(Array.from({ length: laneCount }, worker));
}

async function runPrefetch(): Promise<void> {
  emit({ phase: 'manifest', loaded: 0, total: 0 });
  const manifest = await collectUrls().catch((): AssetManifest => ({
    images: [],
    media: [],
    characterImages: [],
    holdImages: [],
  }));

  const total = manifest.images.length + manifest.media.length;
  let loaded = 0;
  const itemDone = () => {
    loaded += 1;
    emit({ phase: 'download', loaded, total });
  };
  emit({ phase: 'download', loaded, total });

  await Promise.all([
    warmPool(manifest.images, warmImageOne, 6, itemDone),
    warmPool(manifest.media, warmMediaOne, 2, itemDone),
  ]);

  // Prime Pixi's decoded/GPU texture caches too. HTTP bytes are already warm,
  // so MatchScene no longer has to decode every character/hold on first use.
  emit({ phase: 'textures', loaded, total });
  await Promise.allSettled([
    loadClimberTextures(),
    preloadCharacterTextureUrls(manifest.characterImages),
    Promise.all(manifest.holdImages.map((url) => loadHoldSkinTexture(url))),
  ]);

  emit({ phase: 'complete', loaded: total, total });
}

export async function prefetchAssets(
  onProgress?: (next: AssetPrefetchProgress) => void,
): Promise<void> {
  if (onProgress) {
    listeners.add(onProgress);
    onProgress(progress);
  }
  if (!prefetchPromise) prefetchPromise = runPrefetch();
  try {
    await prefetchPromise;
  } finally {
    if (onProgress) listeners.delete(onProgress);
  }
}
